import React, { useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { commandService } from "../services/api";

const CartDrawer: React.FC = () => {
    const { items, removeFromCart, clearCart, totalPrice, isOpen, setIsOpen } = useCart();
    const { user } = useAuth();
    const [snapUsername, setSnapUsername] = useState("");
    const [address, setAddress] = useState("");
    const [phone, setPhone] = useState("");
    const [availableTime, setAvailableTime] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleCheckout = async () => {
        if (!user || items.length === 0 || !snapUsername.trim() || !address.trim() || !phone.trim() || !availableTime) return;
        setIsSubmitting(true);
        try {
            await commandService.create({
                username: user.username,
                product: items.map((i) => i.product),
                product_price: items.map((i) => i.price),
                total_price: totalPrice,
                snap_username: snapUsername,
                address,
                phone,
                available_time: availableTime,
                quantity: items.reduce((sum, i) => sum + i.quantity, 0),
            });
            setSuccess(true);
            clearCart();
            setSnapUsername("");
            setAddress("");
            setPhone("");
            setAvailableTime("");
            setTimeout(() => {
                setSuccess(false);
                setIsOpen(false);
            }, 3000);
        } catch {
            // handle silently
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/60 z-40"
                onClick={() => setIsOpen(false)}
            />

            {/* Drawer */}
            <div className="fixed right-0 top-0 h-full w-full max-w-sm bg-primary-dark border-l border-white/10 z-50 flex flex-col shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <h2 className="text-white font-bold text-lg">Mon panier</h2>
                        {items.length > 0 && (
                            <span className="bg-accent text-primary-dark text-xs font-bold px-2 py-0.5 rounded-full">
                                {items.length}
                            </span>
                        )}
                    </div>
                    <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white transition">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Items */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                    {success ? (
                        <div className="flex flex-col items-center justify-center h-full space-y-3">
                            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <p className="text-white font-semibold text-center">Commande envoyée !</p>
                            <p className="text-gray-400 text-sm text-center">Le livreur a été notifié.</p>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full space-y-3 text-center">
                            <svg className="w-12 h-12 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <p className="text-gray-400">Ton panier est vide</p>
                        </div>
                    ) : (
                        items.map((item, index) => (
                            <div key={index} className="flex items-center space-x-3 bg-primary-light/30 rounded-xl p-3 border border-white/10">
                                <div className="flex-1 min-w-0">
                                    <p className="text-white font-medium text-sm truncate">{item.product.name}</p>
                                    <p className="text-gray-400 text-xs mt-0.5">{item.label}</p>
                                </div>
                                <span className="text-accent font-semibold text-sm whitespace-nowrap">{item.price.toFixed(2)}€</span>
                                <button
                                    onClick={() => removeFromCart(index)}
                                    className="text-gray-500 hover:text-red-400 transition flex-shrink-0"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer checkout */}
                {!success && items.length > 0 && (
                    <div className="px-5 py-4 border-t border-white/10 space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-gray-400 text-sm">Total</span>
                            <span className="text-accent font-bold text-lg">{totalPrice.toFixed(2)}€</span>
                        </div>

                        <input
                            type="text"
                            value={snapUsername}
                            onChange={(e) => setSnapUsername(e.target.value)}
                            className="input-field text-sm"
                            placeholder="Ton pseudo Snapchat"
                        />

                        <input
                            type="text"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            className="input-field text-sm"
                            placeholder="Ton adresse de livraison"
                        />

                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="input-field text-sm"
                            placeholder="Ton numéro de téléphone"
                        />

                        <div>
                            <label className="block text-xs text-gray-400 mb-1.5">Heure de disponibilité</label>
                            <select
                                value={availableTime}
                                onChange={(e) => setAvailableTime(e.target.value)}
                                className="input-field text-sm"
                            >
                                <option value="">Choisir une heure</option>
                                <option value="18:00">18h00</option>
                                <option value="18:30">18h30</option>
                                <option value="19:00">19h00</option>
                                <option value="19:30">19h30</option>
                                <option value="20:00">20h00</option>
                                <option value="20:30">20h30</option>
                                <option value="21:00">21h00</option>
                                <option value="21:30">21h30</option>
                                <option value="22:00">22h00</option>
                                <option value="22:30">22h30</option>
                                <option value="23:00">23h00</option>
                            </select>
                        </div>

                        <button
                            onClick={handleCheckout}
                            disabled={isSubmitting || !snapUsername.trim() || !address.trim() || !phone.trim() || !availableTime}
                            className="w-full py-3 rounded-xl bg-accent hover:bg-accent-light text-primary-dark font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? "Envoi en cours..." : "Commander"}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default CartDrawer;
